import { useEffect, useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import { ChevronDown, Info, Plus, ScanSearch } from 'lucide-react';
import { AccordionContent } from '@radix-ui/react-accordion';
import { datadogLogs } from '@datadog/browser-logs';
import { useQueryClient } from '@tanstack/react-query';
import ModelBadges from 'src/features/messages/message-feed/ModelBadges';
import { useOffice } from 'src/features/plugins/outlook/contexts/OfficeContext';
import { OutlookExpandedViewProvider } from 'src/features/plugins/outlook/contexts/ExpandedViewProvider';
import GeneralFeedback from 'src/features/plugins/outlook/components/Feedback/General';
import Footer from 'src/features/plugins/outlook/components/Footer';
import IntegrationNotFoundCard from 'src/features/plugins/outlook/components/IntegrationNotFoundCard';
import { useIsOfficeAvailable } from 'src/features/plugins/outlook/contexts/OfficeContext';
import EntitiesTab from 'src/features/messages/components/feeds/reading-pane/EntitiesTab';
import InternalNotesTab from 'src/shared/components/internal-notes/InternalNotesTab';
import { useInternalNotes } from 'src/shared/queries/internal-notes/useInternalNotes';
import { ThreadStoreCard } from 'src/shared/components/thread-store';
import { ActualsUpdateType } from 'src/features/messages/types/message.types';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { cleanNullValues, flatEntities } from 'src/shared/lib/entities.utils';
import { Accordion, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import AnimatedEllipsis from 'src/shared/components/ui/loaders/animated-ellipsis';
import { useIntegration } from 'src/features/plugins/queries/useIntegration';
import { useThreadMetadata } from 'src/features/plugins/queries/useThreadMetadata';
import { useThreadStores } from 'src/features/flows/queries/useThreadStores';
import { ThreadStore } from 'src/features/flows/types/threadStore.types';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { AuthLoading } from 'src/features/auth/contexts/AuthLoading';
import { useModels } from 'src/features/models/queries/useModels';
import { useSocket } from 'src/shared/hooks/useSocket';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { useFeatureFlag } from 'src/shared/queries/utils/useFeatureFlag';
import { genQueryKey } from 'src/shared/lib/query.utils';

const TIMEOUT_DELAY = 300;

export default function TaskpanePage() {
  const { logout } = useAuth();
  const [timeoutLoading, setTimeoutLoading] = useState(true);
  const { email, threadId } = useOffice();
  const { data: integration, isPending: integrationPending } = useIntegration(email, 'outlook');
  const isOfficeAvailable = useIsOfficeAvailable();
  const queryClient = useQueryClient();
  const { currentOrg } = useOrg();
  const { data: internalNotesEnabled } = useFeatureFlag('INTERNAL_NOTES');
  const [isNotesComposing, setIsNotesComposing] = useState(false);
  const pageMountTime = useRef(Date.now());

  const tpLog = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.info(message, {
      ...context,
      source: 'TaskpanePage',
      elapsedMs: Date.now() - pageMountTime.current,
    });
  };

  tpLog('[mount] TaskpanePage rendering', {
    email: email ?? 'undefined',
    threadId: threadId ?? 'undefined',
    isOfficeAvailable,
    integrationPending,
    integrationId: integration?.id ?? 'undefined',
    timeoutLoading,
    orgId: currentOrg?.id ?? 'undefined',
  });

  const { data: message, isPending: messagesPending } = useThreadMetadata(integration?.id, threadId);
  const { data: models } = useModels();

  const { data: threadStores, isPending: threadStoresPending } = useThreadStores(integration?.id, threadId);

  const { data: internalNotes } = useInternalNotes(
    internalNotesEnabled ? integration?.id : undefined,
    internalNotesEnabled ? threadId : undefined
  );
  const hasInternalNotes = !!internalNotes?.length;

  // Subscribe to websocket room for this thread
  const roomId = useMemo(() => {
    if (integration?.id && threadId) {
      return ['thread', integration.id, threadId];
    }
    return [];
  }, [integration?.id, threadId]);

  // Listen for thread_store events
  useSocket({
    roomId,
    event: 'thread_store',
    disabled: !integration?.id || !threadId || !currentOrg,
    callback: () => {
      // Refetch threadStores query when thread_store event is received
      if (integration?.id && threadId && currentOrg) {
        const threadStoresQueryKey = ['thread-stores', genQueryKey(currentOrg.id, integration.id, threadId)];
        queryClient.invalidateQueries({ queryKey: threadStoresQueryKey });
      }
    },
  });

  // Listen for predictions events
  useSocket({
    roomId,
    event: 'predictions',
    disabled: !integration?.id || !threadId || !currentOrg,
    callback: () => {
      if (integration?.id && threadId && currentOrg) {
        const threadMetadataQueryKey = ['thread-metadata', genQueryKey(currentOrg.id, integration.id, threadId)];
        queryClient.invalidateQueries({ queryKey: threadMetadataQueryKey });
      }
    },
  });

  const linkedEntityModels = useMemo(() => {
    if (models?.length && message?.integration_id) {
      return models?.filter(
        (model) => (model.isNer && model.inboxes.all) || model.inboxes.ids.includes(message?.integration_id)
      );
    }
    return [];
  }, [message?.integration_id, models]);

  useEffect(() => {
    tpLog('[effect] data state changed', {
      email: email ?? 'undefined',
      threadId: threadId ?? 'undefined',
      integrationPending,
      integrationId: integration?.id ?? 'undefined',
      messagesPending,
      hasMessage: !!message,
      messageModels: message?.models ? Object.keys(message.models).length : 0,
      threadStoresPending,
      threadStoresCount: threadStores?.length ?? 0,
      hasModels: !!models,
      modelsCount: models?.length ?? 0,
      timeoutLoading,
    });
  }, [
    email,
    threadId,
    integrationPending,
    integration,
    messagesPending,
    message,
    threadStoresPending,
    threadStores,
    models,
    timeoutLoading,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      tpLog('[timeout] loading timeout elapsed');
      setTimeoutLoading(false);
    }, TIMEOUT_DELAY);

    return () => clearTimeout(timer);
  }, []);

  const { hasEntities, flattenedEntities } = useMemo(() => {
    if (message?.entities) {
      const cleanedEntities = cleanNullValues(message.entities);
      const hasEntities = Object.keys(cleanedEntities).length > 0;

      const flattenedEntities = flatEntities(cleanedEntities);

      return { hasEntities, flattenedEntities };
    }

    return { hasEntities: false, flattenedEntities: {} };
  }, [message]);

  const handleTagChange = (_action: ActualsUpdateType) => {
    mixpanel.track(`Outlook Add-In - Tag changed`);
  };

  const handleEntityChange = (action: ActualsUpdateType) => {
    mixpanel.track(`Outlook Add-In - Entity Updated - ${action}`);
  };

  if (!email) {
    tpLog('[render] no email, showing "Not logged in"');
    return (
      <GeneralFeedback
        testId="outlook-not-logged-in"
        title="Not logged in"
        description="Please log in to your Outlook account to use this add-in."
        Footer={<Footer className="self-end" onLogout={logout} supportUrl={'https://support.tekst.com'} />}
      />
    );
  }

  if (integrationPending || timeoutLoading) {
    tpLog('[render] showing AuthLoading spinner', { integrationPending, timeoutLoading, email });
    return (
      <div
        data-testid="outlook-integration-loading"
        className="w-[100vw] h-[100vh] flex justify-center items-center grid-container bg-white"
      >
        <div className="w-[100vw] h-[100vh] flex justify-center items-center grid-container">
          <AuthLoading text="Loading integration" from={90} to={100} delay={TIMEOUT_DELAY} />
        </div>
      </div>
    );
  }

  if (!integration) {
    tpLog('[render] no integration found, showing IntegrationNotFoundCard', { email });
    return (
      <GeneralFeedback
        testId="outlook-integration-not-found"
        CardContent={<IntegrationNotFoundCard email={email} />}
        Footer={<Footer className="self-end" onLogout={logout} supportUrl={'https://support.tekst.com'} />}
      />
    );
  }

  const showDownload = isOfficeAvailable && Office.context.platform !== Office.PlatformType.Mac;

  tpLog('[render] rendering main content', {
    integrationId: integration.id,
    threadId: threadId ?? 'undefined',
    hasMessage: !!message,
    messagesPending,
    threadStoresCount: threadStores?.length ?? 0,
    threadStoresPending,
    showDownload,
  });

  return (
    <OutlookExpandedViewProvider>
      <div data-testid="outlook-taskpane-content" className="w-full h-[100vh] flex flex-col bg-muted">
        <div className="w-full h-full flex flex-col items-center overflow-y-auto pt-4">
          <div className="w-[90%] flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-typography-darkgrey text-sm flex items-center gap-2 hide">
                Email analysis
                <WithTooltip delay={100} tooltip={'Insights and classifications for the selected conversation'}>
                  <Info size={16} className="stroke-typography-darkgrey" />
                </WithTooltip>
              </div>
              {messagesPending && !message ? (
                <div className="px-2 text-typography-grey">
                  <AnimatedEllipsis />
                </div>
              ) : message?.models && Object.keys(message.models).length > 0 ? (
                <ModelBadges
                  integrationId={message.integration_id}
                  threadId={message.thread_id}
                  modelOutput={message.models}
                  facetsVersions={message.facets_versions}
                  compact
                  className="pb-1"
                  onTagChange={handleTagChange}
                  canCreateNewTag={false}
                />
              ) : (
                <span className="text-typography-grey text-sm">No data</span>
              )}
              {(hasEntities || linkedEntityModels?.length > 0) && (
                <div className="flex items-center gap-5 text-xs group hover:text-typography-black text-typography-grey w-full">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem key={'entities'} value={'entities'} className="border-none">
                      <AccordionTrigger className="h-5 gap-1 flex items-center justify-start group" headless>
                        <ChevronDown
                          size={14}
                          className="shrink-0 transition-all duration-200 group-data-[state=open]:rotate-180 group-hover:text-typography-black text-typography-grey"
                        />
                        <ScanSearch size={12} className="shrink-0" />
                        {Object.keys(flattenedEntities).length}{' '}
                        {pluralize('entity', Object.keys(flattenedEntities).length)}
                        <div className="flex-grow" />
                      </AccordionTrigger>
                      {message && (
                        <AccordionContent className="my-2">
                          <EntitiesTab message={message} onEntityChange={handleEntityChange} />
                        </AccordionContent>
                      )}
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4 mb-4">
              <div className="text-typography-darkgrey text-sm">Automated actions</div>
              {threadStoresPending ? (
                <div className="px-2 text-typography-grey">
                  <AnimatedEllipsis />
                </div>
              ) : !!threadStores?.length ? (
                threadStores.map((threadStore: ThreadStore) => (
                  <ThreadStoreCard
                    key={threadStore.id}
                    threadStore={threadStore}
                    showDownload={showDownload}
                    onApprovalResult={() => {
                      mixpanel.track('Outlook Add-In - Interaction with Human Approval');
                    }}
                  />
                ))
              ) : (
                <span className="text-typography-grey text-sm">No data</span>
              )}
            </div>
            {internalNotesEnabled && integration?.id && threadId && (hasInternalNotes || isNotesComposing) && (
              <div className="flex flex-col gap-2 mb-4">
                {hasInternalNotes && (
                  <div className="flex items-center justify-between">
                    <span className="text-typography-darkgrey text-sm font-semibold">Internal notes</span>
                    <button
                      onClick={() => setIsNotesComposing(true)}
                      className="flex items-center justify-center w-5 h-5 text-typography-darkgrey hover:text-typography-black transition-colors"
                      aria-label="Add internal note"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                )}
                <InternalNotesTab
                  key={threadId}
                  integrationId={integration.id}
                  threadId={threadId}
                  showComposeButton={false}
                  isComposing={isNotesComposing}
                  onComposingChange={setIsNotesComposing}
                />
              </div>
            )}
          </div>
        </div>
        <Footer
          supportUrl="https://support.tekst.com"
          onLogout={logout}
          className="self-end"
          showHamburgerMenu={internalNotesEnabled}
          threadId={threadId}
          onAddNote={internalNotesEnabled && integration?.id && threadId ? () => setIsNotesComposing(true) : undefined}
        />
      </div>
    </OutlookExpandedViewProvider>
  );
}
